import CategoryIconBar from './CategoryIconBar';
import CategoryTextField from './CategoryTextField';
import { addCategory, updateExistingCategoryColor } from '../actions/addOrUpdateCategory';
import clearNewCategoryState from '../actions/clearNewCategoryState';
import setCategoryColorOnNewCategory from '../actions/setCategoryColorOnNewCategory';
import datapoints, { NewCategoryCreateTrigger } from '../datapoints';
import store from '../store/store';
import { observer } from 'mobx-react';
import { logUsage } from 'owa-analytics';
import {
    CategoryColorPickerHost,
    getStore as getCategoryStore,
    isCategoryNameValid,
    getMasterCategoryList,
} from 'owa-categories';
import * as React from 'react';

const styles = require('./CategoryRow.scss');

export interface CategoryRowProps {
    id: string;
    name: string;
    colorId: string;
    isNewCategoryRow: boolean;
}

@observer
export default class CategoryRow extends React.Component<CategoryRowProps, {}> {
    private textFieldRef;
    private categoryRowRef: HTMLElement;

    render() {
        const categoryContainer = this.props.isNewCategoryRow
            ? styles.newCategoryRowContainer
            : styles.existingCategoryRowContainer;

        const onNewCategoryColorSelected = this.props.isNewCategoryRow
            ? this.updateCategoryColorOnNewCategory
            : this.updateCategoryColorOnExistingCategory;

        return (
            <div
                className={categoryContainer}
                ref={ref => {
                    this.categoryRowRef = ref;
                }}
                role={'group'}
                aria-label={this.props.name}>
                <CategoryColorPickerHost
                    colorPickerIconContainerClass={styles.colorPickerIconContainer}
                    categoryColorId={this.props.colorId}
                    onBlur={this.onBlurOnColorPicker}
                    onNewCategoryColorSelected={onNewCategoryColorSelected}
                    categoryList={getMasterCategoryList()}
                />

                {this.props.isNewCategoryRow ? (
                    <CategoryTextField
                        ref={ref => {
                            this.textFieldRef = ref;
                        }}
                        key="categoryTextField"
                        id={this.props.id}
                        trySaveNewCategory={this.trySaveNewCategory}
                        onBlurOnTextField={this.onBlurOnTextField}
                    />
                ) : (
                    <div className={styles.categoryRowContent}>
                        <span className={styles.categoryRowText} title={this.props.name}>
                            {this.props.name}
                        </span>
                        <CategoryIconBar
                            key={this.props.id}
                            id={this.props.id}
                            name={this.props.name}
                            isNewCategoryPending={this.isNewCategoryPending()}
                        />
                    </div>
                )}
            </div>
        );
    }

    private isNewCategoryPending = () => {
        // Favorite icon is disabled for pending newly created categories, because we don't have the server category id to favorite it
        return !!getCategoryStore().pendingNewCategoriesNameMap[this.props.name];
    };

    private trySaveNewCategory = (createTrigger: NewCategoryCreateTrigger): boolean => {
        // Add category if valid
        // and always clear the store state after adding a new category
        if (isCategoryNameValid(store.newCategory.Name, getMasterCategoryList())) {
            addCategory(this.props.id, store.newCategory.Name, store.newCategory.Color);
            logUsage(
                datapoints.categoryOptionsyNewCategoryCreate.datapoint.name,
                datapoints.categoryOptionsyNewCategoryCreate.datapoint.customData(createTrigger)
            );
            clearNewCategoryState();
            return true;
        }

        return false;
    };

    private onBlurOnTextField = (evt: React.FocusEvent<EventTarget>) => {
        evt.preventDefault();
        let relatedTargetElement = evt.relatedTarget as HTMLElement;

        // In IE relatedTarget is null, using activeElement is the work around instead
        // https://stackoverflow.com/questions/41298948/how-to-use-relatedtarget-or-equivalent-in-ie
        if (!relatedTargetElement) {
            relatedTargetElement = document.activeElement as HTMLElement;
        }

        const isBlurSourceWithinCategoryRow = this.categoryRowRef.contains(relatedTargetElement);

        // If the focus was not being gained by any element withing the category row
        // try committing new category, but always dismiss the new category state
        if (!isBlurSourceWithinCategoryRow) {
            this.trySaveNewCategory(NewCategoryCreateTrigger.OnBlurOnTextField);
            clearNewCategoryState();
        }
    };

    // Called when color picker losses focus
    private onBlurOnColorPicker = () => {
        // If this was a new category row then
        // try committing the category
        // if the row fails to commit then focus on the text field
        if (this.props.isNewCategoryRow) {
            if (!this.trySaveNewCategory(NewCategoryCreateTrigger.OnBlurOnColorPicker)) {
                this.focusTextField();
            }
        }
    };

    // Called when a color is selected for a new category
    private updateCategoryColorOnNewCategory = (categoryColor: string) => {
        setCategoryColorOnNewCategory(categoryColor);
        this.trySaveNewCategory(NewCategoryCreateTrigger.OnSelectColor);
        this.focusTextField();
    };

    // Called when a color is selected for an existing category
    private updateCategoryColorOnExistingCategory = (categoryColor: string) => {
        updateExistingCategoryColor(this.props.id, categoryColor);
    };

    private focusTextField = () => {
        if (this.textFieldRef) {
            this.textFieldRef.focus();
        }
    };
}
